import { useId, useState, useMemo } from 'react'
import { Command } from 'cmdk'
import {
  CheckboxCard,
  Input,
  ScrollArea,
  SearchIcon,
  type CheckedState,
  Text,
  CriticalIcon,
  Notice,
  Flex,
  EmptyState,
} from '@buffer-mono/popcorn'

import ChannelListItem from '~publish/pages/Settings/ChannelGroups/components/ChannelListItem'
import type { ChannelNavChannel } from '../PublishSidebar/useChannels'
import styles from './ChannelSelection.module.css'
import dialogStyles from './ChannelGroupDialog.module.css'
import { clsx } from 'clsx'
import {
  getMultipleChannelTypeRestrictionWarning,
  hasMultipleChannelTypeRestrictions,
} from './utils'

export const ChannelSelection = ({
  filter,
  channels,
  selectedChannelIds,
  handleOnSelectChannel,
  errorMessage,
}: {
  filter: (value: string, search: string, keywords?: string[]) => number
  channels: ChannelNavChannel[]
  selectedChannelIds: string[]
  handleOnSelectChannel: (channelId: string, checked: CheckedState) => void
  errorMessage: string | null
}): JSX.Element => {
  const [search, setSearch] = useState<string>('')
  const searchInputId = useId()
  const showSearch = channels.length >= 10

  /**
   * This is used to show a warning message when the user selects multiple of the channel types
   * which have restriction on the number of channels that can post the same content together.
   * i.e FB Groups, Twitter/X and Instagram business or personal profiles.
   */
  const restrictions = useMemo(() => {
    const selectedChannels = channels.filter((channel) =>
      selectedChannelIds.includes(channel.id),
    )
    return hasMultipleChannelTypeRestrictions(selectedChannels)
  }, [selectedChannelIds, channels])

  return (
    <Command loop filter={filter}>
      {showSearch && (
        <Command.Input
          asChild
          value={search}
          onValueChange={setSearch}
          placeholder="Search channel"
        >
          <Input
            id={searchInputId}
            className={styles.searchInput}
            placeholder="Search channels"
            prefix={<SearchIcon />}
            aria-invalid={!!errorMessage}
            aria-describedby={
              errorMessage ? `${searchInputId}:error` : undefined
            }
          />
        </Command.Input>
      )}
      {errorMessage && (
        <Text
          as="span"
          size="sm"
          id={`${searchInputId}:error`}
          color="critical"
          aria-live="polite"
          role="alert"
          className={clsx(dialogStyles.textError, dialogStyles.bodyAlignment)}
        >
          <CriticalIcon size="xsmall" /> {errorMessage}
        </Text>
      )}
      {restrictions && (
        <Flex className={dialogStyles.bodyAlignment}>
          <Notice variant="warning" className={dialogStyles.noticeWarning}>
            {getMultipleChannelTypeRestrictionWarning(restrictions)}
          </Notice>
        </Flex>
      )}
      <ScrollArea gradientFade={30} className={styles.scrollArea}>
        <Command.Empty className={styles.empty}>
          <EmptyState>
            <EmptyState.Icon>
              <SearchIcon />
            </EmptyState.Icon>
            <EmptyState.Heading>No channels found</EmptyState.Heading>
            <EmptyState.Description>
              Try searching for a different channel.
            </EmptyState.Description>
          </EmptyState>
        </Command.Empty>

        <CheckboxCard.Group
          aria-label="Select channels"
          className={styles.channelList}
        >
          <Command.List className={styles.list}>
            {channels?.map((channel) => (
              <Command.Item
                key={channel.id}
                value={channel.name}
                keywords={[channel.name, channel.service]}
              >
                <ChannelListItem
                  key={channel.id}
                  channel={channel}
                  onChange={handleOnSelectChannel}
                  selected={selectedChannelIds.includes(channel.id)}
                />
              </Command.Item>
            ))}
          </Command.List>
        </CheckboxCard.Group>
      </ScrollArea>
    </Command>
  )
}
